import styled, { useTheme } from 'styled-components';
import Modal, { FullHeightModalContent } from '../../../components/Modal';
import { ParagraphLargeText } from '../../../components/Texts';
import Button from '../../../components/Button';
import { PHONE_MEDIA_QUERY, useMediaQuery } from '../../../common/responsive';
import { formatUSDEnsure2Digits } from '../../../common/currency';
import PrepaidCard from '../../Cards/PrepaidCard';
import { useSelector } from 'react-redux';
import { selectTokenBySymbol } from '../../../redux/slices/tokenSlice';
import { useLazyGetSwapQuoteQuery } from '../../../redux/api/authorized/swap';
import { toTokenAmount, useSolana } from '../../../hooks/useSolana';
import { toast } from 'react-toastify';
import {
  useBroadcastDepositMutation,
  useCreateOrderDepositMutation,
  useListPrepaidCardsQuery,
  usePrepareDepositMutation,
} from '../../../redux/api/authorized/zyptoPrepaidCards';
import { useState } from 'react';
import { Capacitor } from '@capacitor/core';
import { Browser } from '@capacitor/browser';

const ButtonContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 10px;
  width: 100%;
  margin-top: 16px;
`;

const SummaryRow = styled.div`
  display: flex;
  justify-content: space-between;
  width: 100%;
`;

const SummaryTitle = styled.p`
  font-size: 22px;
  font-weight: 600;
  color: ${({ theme }) => theme.text.title};
  @media ${PHONE_MEDIA_QUERY} {
    font-size: 14px;
  }
`;

const SummaryDescription = styled.p`
  font-size: 16px;
  font-weight: 400;
  @media ${PHONE_MEDIA_QUERY} {
    font-size: 12px;
  }
`;

const Line = styled.div`
  margin-top: 40px;
  margin-bottom: 40px;
  width: 100%;
  height: 1px;
  background-color: ${({ theme }) => theme.border.primary};
  @media ${PHONE_MEDIA_QUERY} {
    margin-top: 20px;
    margin-bottom: 20px;
  }
`;

const ERROR_MESSAGE = 'Prepaid card not created.';
const SUCCESS_MESSAGE = 'Prepaid card created.';

const Disclaimer = styled.p`
  font-size: 12px;
  font-weight: 400;
  color: ${({ theme }) => theme.text.light};
  text-align: center;
  margin-bottom: 10px;
`;

const CreatePrepaidCard = ({
  onClose,
  amount,
  selectedToken,
  totalWithFee,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(PHONE_MEDIA_QUERY);
  const [triggerGetSwapQuote] = useLazyGetSwapQuoteQuery();
  const usdcToken = useSelector((state) =>
    selectTokenBySymbol(state, 'solana', 'USDC'),
  );

  const { refetch: refetchPrepaidCards } = useListPrepaidCardsQuery();

  const [cardCreated, setCardCreated] = useState(false);

  const [createOrderDeposit] = useCreateOrderDepositMutation();
  const [prepareDeposit] = usePrepareDepositMutation();
  const [broadcastDeposit] = useBroadcastDepositMutation();
  const [loading, setLoading] = useState(false);

  const { signAndSerializeTransactionWithWallet, primaryWallet, executeSwap } =
    useSolana();

  const createPrepaidCard = async () => {
    setLoading(true);

    if (selectedToken.symbol !== 'USDC') {
      const quoteResult = await triggerGetSwapQuote({
        inputMint: usdcToken.address,
        outputMint: selectedToken.address,
        amount: toTokenAmount(totalWithFee, usdcToken.decimals),
      });
      const neededInputTokenAmount = quoteResult.data.quote.outAmount;
      const swapResponse = await executeSwap({
        inputMint: selectedToken.address,
        outputMint: usdcToken.address,
        amount: neededInputTokenAmount,
      });
      if (!swapResponse) {
        toast.error(ERROR_MESSAGE);
        setLoading(false);
        return;
      }
    }

    const createOrderDepositResponse = await createOrderDeposit({
      amount: parseFloat(amount),
      currency: 'USD',
      tokenSymbol: 'SOL.USDC',
      productId: undefined,
    });

    const ziptoCardId = createOrderDepositResponse?.data?.card?.zyptoCardId;

    if (!ziptoCardId) {
      toast.error(ERROR_MESSAGE);
      setLoading(false);
      return;
    }

    const prepareDepositResponse = await prepareDeposit(ziptoCardId);

    const base64String = await signAndSerializeTransactionWithWallet(
      prepareDepositResponse?.data?.serializedTransaction,
      primaryWallet,
    );

    if (!base64String) {
      toast.error(ERROR_MESSAGE);
      setLoading(false);
      return;
    }

    const broadcastDepositResponse = await broadcastDeposit({
      cardId: ziptoCardId,
      serializedTransaction: base64String,
    });

    if (broadcastDepositResponse.error) {
      toast.error(ERROR_MESSAGE);
      setLoading(false);
    } else {
      toast.success(SUCCESS_MESSAGE);
      setLoading(false);
      setCardCreated(true);
      refetchPrepaidCards();
    }
  };

  return (
    <Modal
      onClose={() => {
        onClose();
        if (onCancel) {
          onCancel();
        }
      }}
    >
      <FullHeightModalContent>
        {cardCreated ? (
          <>
            <ParagraphLargeText style={{ color: theme.text.title }}>
              Prepaid Card Created
            </ParagraphLargeText>
            <PrepaidCard showTapToReveal={false} />
            <SummaryTitle>
              Your new prepaid MasterCard card is ready.
            </SummaryTitle>
            <SummaryDescription style={{ textAlign: 'center' }}>
              You will receive an email from <b>info@obsidian.cards</b> with
              your account activation instructions.
            </SummaryDescription>
            <ButtonContainer>
              <Button
                fullWidth={isMobile}
                onClick={() => {
                  onClose();
                }}
              >
                Continue
              </Button>
            </ButtonContainer>
          </>
        ) : (
          <>
            <ParagraphLargeText style={{ color: theme.text.title }}>
              Prepaid Card Confirmation
            </ParagraphLargeText>
            <PrepaidCard showTapToReveal={false} />
            <SummaryRow>
              <SummaryTitle>Prepaid Card Amount</SummaryTitle>
              <SummaryTitle>{formatUSDEnsure2Digits(amount)}</SummaryTitle>
            </SummaryRow>
            <SummaryRow>
              <SummaryRow>
                <SummaryDescription>Prepaid Card Fee</SummaryDescription>
                <SummaryDescription>
                  + {formatUSDEnsure2Digits(totalWithFee - parseFloat(amount))}
                </SummaryDescription>
              </SummaryRow>
            </SummaryRow>
            <Line />
            <SummaryRow>
              <SummaryTitle>Total:</SummaryTitle>
              <SummaryTitle>
                {formatUSDEnsure2Digits(totalWithFee)}
              </SummaryTitle>
            </SummaryRow>
            <SummaryRow>
              <SummaryDescription>Paying with:</SummaryDescription>
              <SummaryDescription>{selectedToken.symbol}</SummaryDescription>
            </SummaryRow>
            <ButtonContainer>
              <Disclaimer>
                By pressing continue, I accept the{' '}
                <span
                  style={{ textDecoration: 'underline' }}
                  onClick={() => {
                    const link = 'https://obsidian.cards/terms-and-conditions/';
                    if (
                      Capacitor.isNativePlatform() &&
                      Capacitor.isPluginAvailable('Browser')
                    ) {
                      Browser.open({
                        url: link,
                        presentationStyle: 'popover',
                      });
                    } else {
                      window.open(link, '_blank');
                    }
                  }}
                >
                  Prepaid Card Terms of Service
                </span>
                .
              </Disclaimer>
              <Button
                loading={loading}
                fullWidth={isMobile}
                onClick={createPrepaidCard}
              >
                BUY PREPAID CARD
              </Button>
            </ButtonContainer>
          </>
        )}
      </FullHeightModalContent>
    </Modal>
  );
};

export default CreatePrepaidCard;
